import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEnvVar } from '../utils/env';
import { withAuth } from './auth';
import { BAS_API_URL, useBasApi } from './basApi';
import { LEK_API_URL, useLekApi } from './lekApi';
import { useLogApi } from './logApi';

// Determine if we're in development or production
const isDevelopment = process.env.NODE_ENV === 'development';

// For local development, default to localhost
// For production, default to your Render URL
const API_URL =
  getEnvVar('REACT_APP_API_URL') ||
  (isDevelopment ? 'http://localhost:8000' : 'https://fish-dash.onrender.com');
const AUTH_REDIRECT_GUARD_KEY = 'fishDashAuthRedirectInProgress';

// Create axios instance for fish-dash-backend
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(
      `API Error: ${error.response?.status || 'No Status'} from ${error.config?.url || 'Unknown URL'}`
    );
    console.error('Error details:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Custom hook that provides API functions with authentication
export const useApi = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const redirectToLoginOnce = useCallback((reason) => {
    if (typeof window !== 'undefined') {
      const existingReason = window.sessionStorage.getItem(AUTH_REDIRECT_GUARD_KEY);
      if (existingReason) {
        console.warn(
          `Auth redirect already attempted for ${existingReason}; suppressing repeated redirect for ${reason}.`
        );
        return;
      }
      window.sessionStorage.setItem(AUTH_REDIRECT_GUARD_KEY, reason);
    }
    loginWithRedirect({
      appState: {
        returnTo: window.location.pathname + window.location.search,
      },
    });
  }, [loginWithRedirect]);

  // Helper function to handle API calls with authentication
  const apiCall = useCallback(async (apiFunction, ...args) => {
    try {
      console.log('🔐 Getting Auth0 token...');
      const token = await getAccessTokenSilently();
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(AUTH_REDIRECT_GUARD_KEY);
      }
      console.log('✅ Auth0 token received:', token ? 'YES' : 'NO');
      return await apiFunction(...args, token);
    } catch (error) {
      // Check if this is a BAS API or LEK API error by looking at the URL
      const isBasApiError = error.config?.url?.includes(BAS_API_URL) ||
                           error.config?.baseURL?.includes(BAS_API_URL);
      const isLekApiError = error.config?.url?.includes(LEK_API_URL) ||
                           error.config?.baseURL?.includes(LEK_API_URL);
      const isMainApiError = !isBasApiError && !isLekApiError;

      if (error.response?.status === 401) {
        if (isMainApiError) {
          console.log('401 Unauthorized detected, redirecting to login...');
          redirectToLoginOnce('api-401');
        }
      } else if (error.response?.status === 403) {
        if (isMainApiError) {
          console.log('403 Forbidden detected, redirecting to /forbidden...');
          navigate('/forbidden');
        } else {
          console.log(`403 Forbidden detected for ${isBasApiError ? 'BAS' : 'LEK'} API, not redirecting...`);
        }
      } else if (error.message?.includes('Consent required') || error.message?.includes('Login required')) {
        if (isMainApiError) {
          console.log('Auth0 login/consent required, redirecting to login...');
          redirectToLoginOnce('silent-token-consent-or-login-required');
        }
      }
      throw error;
    }
  }, [getAccessTokenSilently, redirectToLoginOnce, navigate]);

  // Stock endpoints
  const getStocks = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/stocks', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Year endpoints
  const getYears = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/years', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Fisheries data endpoints (query: stock_uuid, stock_uuids comma-separated)
  const getFisheriesData = useCallback(async (year = null, stockUuid = null) => {
    return await apiCall(async (token) => {
      const params = {};
      if (year) params.year = year;
      if (stockUuid) params.stock_uuid = stockUuid;
      return await api.get('/fisheries-data', { params, headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Table definition endpoint
  const getTableDefinition = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/table-definition', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Management parameters endpoints
  const getManagementParameters = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/management-parameters', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateManagementAdjustment = useCallback(async (stockUuid, data) => {
    return await apiCall(async (token) => {
      return await api.put(`/management-adjustment/${stockUuid}`, data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updatePStarValue = useCallback(async (stockUuid, data) => {
    return await apiCall(async (token) => {
      return await api.put(`/pstar-value/${stockUuid}`, data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateAllocationRatio = useCallback(async (stockUuid, data) => {
    return await apiCall(async (token) => {
      return await api.put(`/allocation-ratio/${stockUuid}`, data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // BATCH UPDATE FUNCTIONS
  const updateAllPStarValues = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.put('/pstar-value/batch', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateAllManagementAdjustments = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.put('/management-adjustment/batch', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateAllRebuildingTimes = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.put('/rebuilding-time/batch', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Rebuilding parameters endpoints
  const getRebuildingParameters = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/rebuilding-parameters', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getStockRebuildingTime = useCallback(async (stockUuid) => {
    return await apiCall(async (token) => {
      return await api.get(`/rebuilding-time/${stockUuid}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateRebuildingTime = useCallback(async (stockUuid, data) => {
    return await apiCall(async (token) => {
      return await api.put(`/rebuilding-time/${stockUuid}`, data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Batch update and other endpoints
  const batchUpdateParameters = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.post('/batch-update-parameters', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getMesoscaleDynamicsSettings = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/mesoscale-dynamics', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateMesoscaleDynamics = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.put('/mesoscale-dynamics', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const refreshDatabase = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.post('/refresh-database', {}, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Productivity Enhancement (Zeta) endpoints
  const getProductivityEnhancementSettings = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/productivity-enhancement', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const updateProductivityEnhancement = useCallback(async (data) => {
    return await apiCall(async (token) => {
      return await api.put('/productivity-enhancement', data, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Environmental factors endpoint
  const getEnvironmentalFactors = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/environmental-factors', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Model run endpoints
  const runModel = useCallback(async (params) => {
    return await apiCall(async (token) => {
      return await api.post('/run-model', params, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const runForecast = useCallback(async (params) => {
    return await apiCall(async (token) => {
      return await api.post('/run-forecast', params, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // BAS, LEK, and Log Service API functions (from separate modules)
  const basApiFunctions = useBasApi(apiCall);
  const lekApiFunctions = useLekApi(apiCall);
  const logApiFunctions = useLogApi(apiCall);

  // Stock Summary endpoint
  const getStockSummary = useCallback(async (year, timeCategory = null, minDepth = null, maxDepth = null, sedimentType = null) => {
    return await apiCall(async (token) => {
      const params = {};
      if (timeCategory) params.time_category = timeCategory;
      if (minDepth !== null) params.min_depth = minDepth;
      if (maxDepth !== null) params.max_depth = maxDepth;
      if (sedimentType) params.predominant_sediment_type = sedimentType;
      const queryString = Object.keys(params).length > 0 ? `?${new URLSearchParams(params).toString()}` : '';
      return await api.get(`/stock_summary/${year}${queryString}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getFisheriesDataBatch = useCallback(async (years = [], stockUuids = []) => {
    return await apiCall(async (token) => {
      const params = {};
      if (years && years.length) params.years = years.join(',');
      if (stockUuids && stockUuids.length) params.stock_uuids = stockUuids.join(',');
      return await api.get('/fisheries-data', { params, headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Add these to your existing imports and useApi hook

// ==================== FISHING ECONOMICS API FUNCTIONS ====================

// Vessel CRUD endpoints
const getFishingVessels = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/vessels', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getFishingVessel = useCallback(async (vesselName) => {
  return await apiCall(async (token) => {
    return await api.get(`/fishing/vessels/${vesselName}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const createFishingVessel = useCallback(async (vesselData) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/vessels', vesselData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateFishingVessel = useCallback(async (vesselName, vesselData) => {
  return await apiCall(async (token) => {
    return await api.put(`/fishing/vessels/${vesselName}`, vesselData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const deleteFishingVessel = useCallback(async (vesselName) => {
  return await apiCall(async (token) => {
    return await api.delete(`/fishing/vessels/${vesselName}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Port CRUD endpoints
const getFishingPorts = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/ports', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getFishingPort = useCallback(async (portName) => {
  return await apiCall(async (token) => {
    return await api.get(`/fishing/ports/${portName}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const createFishingPort = useCallback(async (portData) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/ports', portData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateFishingPort = useCallback(async (portName, portData) => {
  return await apiCall(async (token) => {
    return await api.put(`/fishing/ports/${portName}`, portData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const deleteFishingPort = useCallback(async (portName) => {
  return await apiCall(async (token) => {
    return await api.delete(`/fishing/ports/${portName}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Target Distribution CRUD endpoints
const getTargetDistributions = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/target-distributions', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getTargetDistribution = useCallback(async (targetSpecies) => {
  return await apiCall(async (token) => {
    return await api.get(`/fishing/target-distributions/${targetSpecies}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const createTargetDistribution = useCallback(async (distributionData) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/target-distributions', distributionData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateTargetDistribution = useCallback(async (targetSpecies, distributionData) => {
  return await apiCall(async (token) => {
    return await api.put(`/fishing/target-distributions/${targetSpecies}`, distributionData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const deleteTargetDistribution = useCallback(async (targetSpecies) => {
  return await apiCall(async (token) => {
    return await api.delete(`/fishing/target-distributions/${targetSpecies}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Processing Rates CRUD endpoints
const getProcessingRates = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/processing-rates', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getProcessingRate = useCallback(async (portName, species) => {
  return await apiCall(async (token) => {
    return await api.get(`/fishing/processing-rates/${portName}/${species}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const createProcessingRate = useCallback(async (rateData) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/processing-rates', rateData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateProcessingRate = useCallback(async (portName, species, rateData) => {
  return await apiCall(async (token) => {
    return await api.put(`/fishing/processing-rates/${portName}/${species}`, rateData, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const deleteProcessingRate = useCallback(async (portName, species) => {
  return await apiCall(async (token) => {
    return await api.delete(`/fishing/processing-rates/${portName}/${species}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Economics Calculation endpoints
const calculateTripEconomics = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/calculate/trip-economics', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const simulateFishingSeason = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/simulate/season', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const calculateProcessingEconomics = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/calculate/processing-economics', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const runSensitivityAnalysis = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/analyze/sensitivity', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const runBatchCalculations = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/fishing/calculate/batch', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Utility endpoints
const getFishingOptions = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/options', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getFishingStatistics = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/statistics', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getTripResults = useCallback(async (filters = {}) => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/trip-results', { params: filters, headers: withAuth({}, token) });
  });
}, [apiCall]);

const getSeasonSummaries = useCallback(async (filters = {}) => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/season-summaries', { params: filters, headers: withAuth({}, token) });
  });
}, [apiCall]);

const runMultiYearFleetSimulation = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/simulate/multi-year-fleet', params, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getAssetValuations = useCallback(async (filters = {}) => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/asset-valuations', { params: filters, headers: withAuth({}, token) });
  });
}, [apiCall]);

const getQuotaMarketTransactions = useCallback(async (filters = {}) => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/quota-transactions', { params: filters, headers: withAuth({}, token) });
  });
}, [apiCall]);

const getQuotaTradingActivity = useCallback(async (filters = {}) => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/quota-trading-activity', { params: filters, headers: withAuth({}, token) });
  });
}, [apiCall]);

const getFleetQuotaUtilization = useCallback(async (filters = {}) => {
  console.log('API function called with:', filters); // Add this debug line
  return await apiCall(async (token) => {
    console.log('Making request with params:', filters); // Add this debug line
    return await api.get('/fishing/fleet-quota-utilization', { 
      params: filters, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Session management endpoints
const getSessions = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/sessions', { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Add these to your existing useApi hook return object
const getAclSensitivityAnalyses = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/fishing/acl-sensitivity/analyses', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getAclAnalysisBySession = useCallback(async (baseSessionName) => {
  return await apiCall(async (token) => {
    return await api.get(`/fishing/acl-sensitivity/analyses/${baseSessionName}`, { headers: withAuth({}, token) });
  });
}, [apiCall]);

const getChokeSpeciesAnalysis = useCallback(async (baseSessionName = null, utilizationThreshold = 90.0) => {
  return await apiCall(async (token) => {
    const params = {};
    if (baseSessionName) params.base_session_name = baseSessionName;
    if (utilizationThreshold) params.utilization_threshold = utilizationThreshold;
    return await api.get('/fishing/acl-sensitivity/choke-species-analysis', { params, headers: withAuth({}, token) });
  });
}, [apiCall]);


// Natural Mortality Settings endpoints
const getNaturalMortalitySettings = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/natural-mortality-settings', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateNaturalMortalitySettings = useCallback(async (data) => {
  return await apiCall(async (token) => {
    return await api.put('/natural-mortality-settings', data, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Natural Mortality Parameters endpoints
const getNaturalMortalityParameters = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/natural-mortality-parameters', { headers: withAuth({}, token) });
  });
}, [apiCall]);

const updateNaturalMortalityParameters = useCallback(async (stockUuid, data) => {
  return await apiCall(async (token) => {
    return await api.put(`/natural-mortality-parameters/${stockUuid}`, data, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// Add this to your useApi hook in api.js
const updateAllNaturalMortalityParameters = useCallback(async (data) => {
  return await apiCall(async (token) => {
    return await api.put('/natural-mortality-parameters/batch', data, { headers: withAuth({}, token) });
  });
}, [apiCall]);

// ==================== SCALLOP API FUNCTIONS ====================

// CASA Assessment endpoints
const getCasaRuns = useCallback(async (limit = 50) => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/casa/runs', { 
      params: { limit }, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaRunDetails = useCallback(async (runId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/casa/runs/${runId}`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaParameters = useCallback(async (runId, parameterType = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (parameterType) params.parameter_type = parameterType;
    return await api.get(`/scallop/casa/runs/${runId}/parameters`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaAbundance = useCallback(async (runId, year = null, sizeBinMm = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (sizeBinMm) params.size_bin_mm = sizeBinMm;
    return await api.get(`/scallop/casa/runs/${runId}/abundance`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaAnnualSummary = useCallback(async (runId, year = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    return await api.get(`/scallop/casa/runs/${runId}/annual-summary`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaSpatialAllocation = useCallback(async (runId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/casa/runs/${runId}/spatial-allocation`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaBiology = useCallback(async (runId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/casa/runs/${runId}/biology`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getCasaDiagnostics = useCallback(async (runId, diagnosticType = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (diagnosticType) params.diagnostic_type = diagnosticType;
    return await api.get(`/scallop/casa/runs/${runId}/diagnostics`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const compareCasaRuns = useCallback(async (runIds) => {
  return await apiCall(async (token) => {
    return await api.post('/scallop/casa/compare', runIds, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// SAMS Projection endpoints
const getSamsScenarios = useCallback(async (casaRunId = null, limit = 50) => {
  return await apiCall(async (token) => {
    const params = { limit };
    if (casaRunId) params.casa_run_id = casaRunId;
    return await api.get('/scallop/sams/scenarios', { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsScenarioDetails = useCallback(async (scenarioId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/sams/scenarios/${scenarioId}`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAbundance = useCallback(async (scenarioId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/abundance`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAnnualSummary = useCallback(async (scenarioId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/annual-summary`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsEconomics = useCallback(async (scenarioId, year = null, vesselClass = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (vesselClass) params.vessel_class = vesselClass;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/economics`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsEffortAllocation = useCallback(async (scenarioId, year = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/effort-allocation`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAreaClosures = useCallback(async (scenarioId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/area-closures`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const compareSamsScenarios = useCallback(async (scenarioIds) => {
  return await apiCall(async (token) => {
    return await api.post('/scallop/sams/compare', scenarioIds, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Survey Data endpoints
const getScallopSurveyYears = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/survey/years', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopSurveyStrata = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/survey/strata', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopSurveyBiomass = useCallback(async (year = null, stratum = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (stratum) params.stratum = stratum;
    return await api.get('/scallop/survey/biomass', { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopLengthFrequency = useCallback(async (year, stratum = null) => {
  return await apiCall(async (token) => {
    const params = { year };
    if (stratum) params.stratum = stratum;
    return await api.get('/scallop/survey/length-frequency', { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopCommercialLandings = useCallback(async (startYear = null, endYear = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (startYear) params.start_year = startYear;
    if (endYear) params.end_year = endYear;
    return await api.get('/scallop/commercial/landings', { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Bridge and Utility endpoints
const getCasaToSamsBridge = useCallback(async (runId, terminalYear) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/casa-to-sams/${runId}`, { 
      params: { terminal_year: terminalYear }, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAreas = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/areas', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopVesselClasses = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/vessel-classes', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopDatabaseStatus = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/database-status', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getScallopSystemSummary = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/summary', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const runSAMSProjection = useCallback(async (params) => {
  return await apiCall(async (token) => {
    return await api.post('/scallop/sams/run-projection', params, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAreaMetrics = useCallback(async (scenarioId, year = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/area-metrics`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

const getSamsAreaSummaryTable = useCallback(async (scenarioId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/area-summary-table`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);



// ==================== SCALLOP ECONOMICS API FUNCTIONS ====================

// Ex-vessel value endpoints
const getSamsExVesselValue = useCallback(async (scenarioId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/ex-vessel-value`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Jobs impact endpoints
const getSamsJobsImpact = useCallback(async (scenarioId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/jobs-impact`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Meals impact endpoints
const getSamsMealsImpact = useCallback(async (scenarioId, year = null, samsArea = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (year) params.year = year;
    if (samsArea) params.sams_area = samsArea;
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/meals-impact`, { 
      params, 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Economic summary endpoint
const getSamsEconomicSummary = useCallback(async (scenarioId) => {
  return await apiCall(async (token) => {
    return await api.get(`/scallop/sams/scenarios/${scenarioId}/economic-summary`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Economic comparison endpoint
const compareSamsScenarioEconomics = useCallback(async (scenarioIds) => {
  return await apiCall(async (token) => {
    return await api.post('/scallop/sams/compare-economics', null, { 
      params: { scenario_ids: scenarioIds },
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);


// ==================== TRAWL SURVEY API FUNCTIONS ====================

const getTrawlSurveyCruises = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/trawl-survey/cruise', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

const getTrawlSurveySpecies = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/trawl-survey/species', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

const getTrawlSurveyCruise = useCallback(async (cruiseId) => {
  return await apiCall(async (token) => {
    const params = {};
    if (cruiseId) params.id = cruiseId;
    return await api.get('/trawl-survey/cruise', {
      params,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

const getTrawlSurveyCatch = useCallback(async (catchId) => {
  return await apiCall(async (token) => {
    return await api.get('/trawl-survey/catch', {
      params: { 'catch-id': catchId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

const getTrawlSurveyCatchesByCruise = useCallback(async (cruiseId) => {
  return await apiCall(async (token) => {
    return await api.get('/trawl-survey/catch', {
      params: { 'cruise-id': cruiseId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

const getTrawlSurveyCatchesByStation = useCallback(async (stationId) => {
  return await apiCall(async (token) => {
    return await api.get('/trawl-survey/catch', {
      params: { 'station-id': stationId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

  // WHAM endpoints
  const getWhamStocks = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/wham/stocks', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getWhamSessions = useCallback(async (stockUuid) => {
    return await apiCall(async (token) => {
      return await api.get(`/wham/sessions/${stockUuid}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getWhamSession = useCallback(async (sessionId) => {
    return await apiCall(async (token) => {
      return await api.get(`/wham/session/${sessionId}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const deleteWhamSession = useCallback(async (sessionId) => {
    return await apiCall(async (token) => {
      return await api.delete(`/wham/session/${sessionId}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const runWhamProjection = useCallback(async (stockUuid, params) => {
    return await apiCall(async (token) => {
      return await api.post(`/wham/run-projection/${stockUuid}`, params, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getLatestWhamProjection = useCallback(async (stockUuid) => {
    return await apiCall(async (token) => {
      return await api.get(`/wham/projection/${stockUuid}/latest`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getWhamHealth = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/wham/health', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const clearWhamCache = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.post('/wham/cache/clear', {}, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

const getLpueOptions = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/scallop/sams/lpue_options', {  // Changed from lpue-options to lpue_options
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

  // ERDDAP endpoints
  const getBottomTemperature = useCallback(async (params) => {
    return await apiCall(async (token) => {
      const {
        startDate,
        endDate,
        minLat = 34,
        maxLat = 45,
        minLon = -83,
        maxLon = -66
      } = params || {};
      
      return await api.get('/erddap/bottom-temperature', {
        params: { startDate, endDate, minLat, maxLat, minLon, maxLon },
        headers: withAuth({}, token),
        timeout: 30000
      });
    });
  }, [apiCall]);

  // Atlantic States endpoints
  const getAtlanticStates = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/atlantic-states', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getAtlanticStatesSummary = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/atlantic-states/stats/summary', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  const getAtlanticState = useCallback(async (state) => {
    return await apiCall(async (token) => {
      return await api.get(`/atlantic-states/${state}`, { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Cold Storage endpoints
  const getColdStorage = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/cold-storage', { headers: withAuth({}, token) });
    });
  }, [apiCall]);

  // Ports endpoints
  const getPorts = useCallback(async () => {
    return await apiCall(async (token) => {
      return await api.get('/ports', { headers: withAuth({}, token) });
    });
  }, [apiCall]);


  // ==================== LOBSTER FISHERY API FUNCTIONS ====================

// Run lobster projection
const runLobsterProjection = useCallback(async (params) => {
  console.log('[LOBSTER API] runLobsterProjection called', { params });
  const result = await apiCall(async (token) => {
    const res = await api.post('/lobster/run', params, {
      headers: withAuth({}, token)
    });
    console.log('[LOBSTER API] runLobsterProjection response', { status: res?.status, data: res?.data });
    return res;
  });
  return result;
}, [apiCall]);

// Get all lobster scenarios
const getLobsterScenarios = useCallback(async (limit = null) => {
  console.log('[LOBSTER API] getLobsterScenarios called', { limit });
  const result = await apiCall(async (token) => {
    const params = {};
    if (limit) params.limit = limit;
    const res = await api.get('/lobster/scenarios', {
      params,
      headers: withAuth({}, token)
    });
    console.log('[LOBSTER API] getLobsterScenarios response', {
      status: res?.status,
      dataKeys: res?.data ? Object.keys(res.data) : [],
      resultsCount: Array.isArray(res?.data) ? res.data.length : res?.data?.results?.length ?? 'N/A',
    });
    return res;
  });
  return result;
}, [apiCall]);

// Get specific lobster scenario
const getLobsterScenario = useCallback(async (projectionId) => {
  console.log('[LOBSTER API] getLobsterScenario called', { projectionId });
  const result = await apiCall(async (token) => {
    const res = await api.get(`/lobster/scenarios/${projectionId}`, {
      headers: withAuth({}, token)
    });
    console.log('[LOBSTER API] getLobsterScenario response', {
      projectionId,
      status: res?.status,
      hasAnnualResults: !!res?.data?.annual_results,
      annualResultsLength: res?.data?.annual_results?.length ?? 0,
    });
    return res;
  });
  return result;
}, [apiCall]);

// Get scenarios by name
const getLobsterScenariosByName = useCallback(async (scenarioName) => {
  return await apiCall(async (token) => {
    return await api.get(`/lobster/scenarios/name/${scenarioName}`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Delete lobster scenario
const deleteLobsterScenario = useCallback(async (projectionId) => {
  return await apiCall(async (token) => {
    return await api.delete(`/lobster/scenarios/${projectionId}`, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Compare lobster scenarios
const compareLobsterScenarios = useCallback(async (projectionIds) => {
  return await apiCall(async (token) => {
    return await api.post('/lobster/compare', { projection_ids: projectionIds }, { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Get lobster database stats
const getLobsterDatabaseStats = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/lobster/database/stats', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Lobster health check
const getLobsterHealth = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/lobster/health', { 
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Get historical bait market data
const getLobsterBaitMarketHistorical = useCallback(async (modelVersion = 'four_regime_v1') => {
  console.log('[LOBSTER API] getLobsterBaitMarketHistorical called', { modelVersion });
  const result = await apiCall(async (token) => {
    const res = await api.get('/lobster/bait-market/historical', {
      params: { model_version: modelVersion },
      headers: withAuth({}, token)
    });
    console.log('[LOBSTER API] getLobsterBaitMarketHistorical response', { status: res?.status, hasData: !!res?.data?.data });
    return res;
  });
  return result;
}, [apiCall]);

// Get bait market validation metrics
const getLobsterBaitMarketValidation = useCallback(async (modelVersion = 'four_regime_v1') => {
  return await apiCall(async (token) => {
    return await api.get('/lobster/bait-market/validation', { 
      params: { model_version: modelVersion },
      headers: withAuth({}, token) 
    });
  });
}, [apiCall]);

// Get bait market regime summaries
const getLobsterBaitMarketRegimes = useCallback(async (modelVersion = 'four_regime_v1') => {
  return await apiCall(async (token) => {
    return await api.get('/lobster/bait-market/regimes', {
      params: { model_version: modelVersion },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Page layout endpoint
const getPageLayout = useCallback(async (pageType, resourceId) => {
  return await apiCall(async (token) => {
    return await api.get('/op/pages/layout', {
      params: { page_type: pageType, resource_id: resourceId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Update page camera endpoint - PATCH to update camera for a page (page_type + resource_id)
const updatePageCamera = useCallback(async (pageType, resourceId, body) => {
  return await apiCall(async (token) => {
    return await api.patch('/op/pages/camera', body, {
      params: { page_type: pageType, resource_id: resourceId },
      headers: withAuth({ 'Content-Type': 'application/json' }, token)
    });
  });
}, [apiCall]);

// Update page camera for page and all descendants
const updatePageCameraWithDescendants = useCallback(async (pageType, resourceId, body) => {
  return await apiCall(async (token) => {
    return await api.patch('/op/pages/camera-with-descendants', body, {
      params: { page_type: pageType, resource_id: resourceId },
      headers: withAuth({ 'Content-Type': 'application/json' }, token)
    });
  });
}, [apiCall]);

// Page children endpoint - returns child pages (e.g. fishery management plan steps for a stock)
const getPageChildren = useCallback(async (pageType, resourceId) => {
  return await apiCall(async (token) => {
    return await api.get('/op/pages/children', {
      params: { page_type: pageType, resource_id: resourceId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Page component slugs endpoint - returns list of component slugs for a page/step
const getPageComponentSlugs = useCallback(async (pageId) => {
  return await apiCall(async (token) => {
    return await api.get('/op/pages/component-slugs', {
      params: { page_id: pageId },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Panel sections endpoint - returns all known panel sections
const getPanelSections = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/op/panel-sections', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Create panel section endpoint - POST to create a new UI panel section
const createPanelSection = useCallback(async (body) => {
  return await apiCall(async (token) => {
    return await api.post('/op/panel-sections', body, {
      headers: withAuth({ 'Content-Type': 'application/json' }, token)
    });
  });
}, [apiCall]);

// Page components endpoint - returns all known components
const getPageComponents = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/op/components', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Update panel layout - PUT to save panel sections and components (slug-based)
const updatePanelLayout = useCallback(async (pageType, resourceId, panelSlug, body) => {
  return await apiCall(async (token) => {
    return await api.put('/op/pages/panel-layout', body, {
      params: { page_type: pageType, resource_id: resourceId, panel_slug: panelSlug },
      headers: withAuth({ 'Content-Type': 'application/json' }, token)
    });
  });
}, [apiCall]);

// Create component endpoint - POST to create a new UI component
const createComponent = useCallback(async (body) => {
  return await apiCall(async (token) => {
    return await api.post('/op/components', body, {
      headers: withAuth({ 'Content-Type': 'application/json' }, token)
    });
  });
}, [apiCall]);

// Delete component endpoint - DELETE by component slug
const deleteComponent = useCallback(async (componentSlug) => {
  return await apiCall(async (token) => {
    return await api.delete(`/op/components/${encodeURIComponent(componentSlug)}`, {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Delete panel section endpoint - DELETE by panel section slug
const deletePanelSection = useCallback(async (panelSlug) => {
  return await apiCall(async (token) => {
    return await api.delete(`/op/panel-sections/${encodeURIComponent(panelSlug)}`, {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Page hierarchy endpoint - returns tree of regions/plans/stocks
// max_depth is optional: 'region' | 'plan' | 'stock' limits tree depth; omit for full tree
const getPageHierarchy = useCallback(async (maxDepth = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (maxDepth != null) params.max_depth = maxDepth;
    return await api.get('/op/pages/hierarchy', {
      params,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Region by slug
const getRegionBySlug = useCallback(async (fisheryManagementRegionSlug) => {
  return await apiCall(async (token) => {
    return await api.get('/op/regions/by-slug', {
      params: { fishery_management_region_slug: fisheryManagementRegionSlug },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Plans by slugs
const getPlansBySlugs = useCallback(async (fisheryManagementRegionSlug, fisheryManagementPlanSlug) => {
  return await apiCall(async (token) => {
    return await api.get('/op/plans/by-slugs', {
      params: { 
        fishery_management_region_slug: fisheryManagementRegionSlug,
        fishery_management_plan_slug: fisheryManagementPlanSlug
       },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// Stocks by slugs
const getStocksBySlugs = useCallback(async (stockSlug, fisheryManagementPlanSlug, fisheryManagementRegionSlug) => {
  return await apiCall(async (token) => {
    return await api.get('/op/stocks/by-slugs', {
      params: {
        stock_slug: stockSlug,
        fishery_management_plan_slug: fisheryManagementPlanSlug,
        fishery_management_region_slug: fisheryManagementRegionSlug
      },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);


// ==================== AIS SCALLOP FLEET API FUNCTIONS ====================

// AIS Fleet Summary
const getAISFleetSummary = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/fleet/summary', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Vessel List
const getAISVesselList = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/fleet/vessels', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Daily Fleet Summary
const getAISDailyFleetSummary = useCallback(async (startDate = null, endDate = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return await api.get('/ais-scallop/fleet/daily', {
      params,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Vessel Positions
const getAISPositions = useCallback(async (params = {}) => {
  return await apiCall(async (token) => {
    const queryParams = {};
    if (params.mmsi) queryParams.mmsi = params.mmsi;
    if (params.startDate) queryParams.start_date = params.startDate;
    if (params.endDate) queryParams.end_date = params.endDate;
    if (params.atSeaOnly !== undefined) queryParams.at_sea_only = params.atSeaOnly;
    if (params.fishingOnly !== undefined) queryParams.fishing_only = params.fishingOnly;
    if (params.scallopOnly !== undefined) queryParams.scallop_only = params.scallopOnly;
    if (params.limit) queryParams.limit = params.limit;
    return await api.get('/ais-scallop/positions', {
      params: queryParams,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Hourly Activity (for timeline charts)
const getAISHourlyActivity = useCallback(async (startDate = null, endDate = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return await api.get('/ais-scallop/activity/hourly', {
      params,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Single Vessel Daily Summary
const getAISVesselDaily = useCallback(async (mmsi) => {
  return await apiCall(async (token) => {
    return await api.get(`/ais-scallop/vessel/${mmsi}/daily`, {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Ports List
const getAISPorts = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/ports', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Port Summary
const getAISPortSummary = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/ports/summary', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Port Activity (daily)
const getAISPortActivity = useCallback(async (portName = null, startDate = null, endDate = null) => {
  return await apiCall(async (token) => {
    const params = {};
    if (portName) params.port_name = portName;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return await api.get('/ais-scallop/ports/activity', {
      params,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Trips List
const getAISTrips = useCallback(async (params = {}) => {
  return await apiCall(async (token) => {
    const queryParams = {};
    if (params.mmsi) queryParams.mmsi = params.mmsi;
    if (params.minDurationHours) queryParams.min_duration_hours = params.minDurationHours;
    if (params.limit) queryParams.limit = params.limit;
    return await api.get('/ais-scallop/trips', {
      params: queryParams,
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Trip Positions (for map visualization)
const getAISTripPositions = useCallback(async (mmsi, tripStart, tripEnd) => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/trips/positions', {
      params: {
        mmsi,
        trip_start: tripStart,
        trip_end: tripEnd
      },
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);

// AIS Health Check
const getAISHealth = useCallback(async () => {
  return await apiCall(async (token) => {
    return await api.get('/ais-scallop/health', {
      headers: withAuth({}, token)
    });
  });
}, [apiCall]);


  return {
    getStocks,
    getYears,
    getFisheriesData,
    getTableDefinition,
    getManagementParameters,
    updateManagementAdjustment,
    updatePStarValue,
    updateAllocationRatio,
    updateAllPStarValues,
    updateAllManagementAdjustments,
    updateAllRebuildingTimes,
    getRebuildingParameters,
    getStockRebuildingTime,
    updateRebuildingTime,
    batchUpdateParameters,
    getMesoscaleDynamicsSettings,
    updateMesoscaleDynamics,
    refreshDatabase,
    getProductivityEnhancementSettings,
    updateProductivityEnhancement,
    getEnvironmentalFactors,
    runModel,
    runForecast,
    ...basApiFunctions,
    ...lekApiFunctions,
    ...logApiFunctions,
    getStockSummary,
    getFisheriesDataBatch,
    getFishingVessels,
    getFishingVessel,
    createFishingVessel,
    updateFishingVessel,
    deleteFishingVessel,
    getFishingPorts,
    getFishingPort,
    createFishingPort,
    updateFishingPort,
    deleteFishingPort,
    getTargetDistributions,
    getTargetDistribution,
    createTargetDistribution,
    updateTargetDistribution,
    deleteTargetDistribution,
    getProcessingRates,
    getProcessingRate,
    createProcessingRate,
    updateProcessingRate,
    deleteProcessingRate,
    calculateTripEconomics,
    simulateFishingSeason,
    calculateProcessingEconomics,
    runSensitivityAnalysis,
    runBatchCalculations,
    getFishingOptions,
    getFishingStatistics,
    getTripResults,
    getSeasonSummaries,
    runMultiYearFleetSimulation,
    getAssetValuations,
    getQuotaMarketTransactions,
    getQuotaTradingActivity,
    getFleetQuotaUtilization,
    getSessions,
    getAclSensitivityAnalyses,
    getAclAnalysisBySession,
    getChokeSpeciesAnalysis,
    getNaturalMortalitySettings,
    updateNaturalMortalitySettings,
    getNaturalMortalityParameters,
    updateNaturalMortalityParameters,
    updateAllNaturalMortalityParameters,

    // Add scallop functions here:
    getCasaRuns,
    getCasaRunDetails,
    getCasaParameters,
    getCasaAbundance,
    getCasaAnnualSummary,
    getCasaSpatialAllocation,
    getCasaBiology,
    getCasaDiagnostics,
    compareCasaRuns,
    getSamsScenarios,
    getSamsScenarioDetails,
    getSamsAbundance,
    getSamsAnnualSummary,
    getSamsEconomics,
    getSamsEffortAllocation,
    getSamsAreaClosures,
    compareSamsScenarios,
    getScallopSurveyYears,
    getScallopSurveyStrata,
    getScallopSurveyBiomass,
    getScallopLengthFrequency,
    getScallopCommercialLandings,
    getCasaToSamsBridge,
    getSamsAreas,
    getScallopVesselClasses,
    getScallopDatabaseStatus,
    getScallopSystemSummary,
    runSAMSProjection,
    getSamsAreaMetrics,
    getSamsAreaSummaryTable,
      // ADD THE NEW FUNCTIONS HERE ⬇️
    getSamsExVesselValue,
    getSamsJobsImpact,
    getSamsMealsImpact,
    getSamsEconomicSummary,
    compareSamsScenarioEconomics,
    getLpueOptions,

    // Trawl Survey
    getTrawlSurveyCruises,
    getTrawlSurveySpecies,
    getTrawlSurveyCruise,
    getTrawlSurveyCatch,
    getTrawlSurveyCatchesByCruise,
    getTrawlSurveyCatchesByStation,
    // WHAM endpoints
    getWhamStocks,
    getWhamSessions,
    getWhamSession,
    deleteWhamSession,
    runWhamProjection,
    getLatestWhamProjection,
    getWhamHealth,
    clearWhamCache,

    // ERDDAP endpoints
    getBottomTemperature,

    // Atlantic States endpoints
    getAtlanticStates,
    getAtlanticStatesSummary,
    getAtlanticState,

    // Cold Storage endpoints
    getColdStorage,

    // Ports endpoints
    getPorts,

    // Lobster Fishery endpoints
    runLobsterProjection,
    getLobsterScenarios,
    getLobsterScenario,
    getLobsterScenariosByName,
    deleteLobsterScenario,
    compareLobsterScenarios,
    getLobsterDatabaseStats,
    getLobsterHealth,
    getLobsterBaitMarketHistorical,
    getLobsterBaitMarketValidation,
    getLobsterBaitMarketRegimes,

    // AIS Scallop Fleet endpoints
    getAISFleetSummary,
    getAISVesselList,
    getAISDailyFleetSummary,
    getAISPositions,
    getAISHourlyActivity,
    getAISVesselDaily,
    getAISPorts,
    getAISPortSummary,
    getAISPortActivity,
    getAISTrips,
    getAISTripPositions,
    getAISHealth,
    getPageLayout,
    updatePageCamera,
    updatePageCameraWithDescendants,
    updatePanelLayout,
    getPageChildren,
    getPageComponentSlugs,
    getPanelSections,
    createPanelSection,
    deletePanelSection,
    getPageComponents,
    createComponent,
    deleteComponent,
    getPageHierarchy,
    getRegionBySlug,
    getPlansBySlugs,
    getStocksBySlugs,


  };
};

// Legacy functions for backward compatibility (without authentication)
// These can be removed once all components are updated to use useApi



// Stock endpoints
export const getStocks = async (accessToken) => {
  return await api.get('/stocks', { headers: withAuth({}, accessToken) });
}

// Year endpoints
export const getYears = async (accessToken) => {
  return await api.get('/years', { headers: withAuth({}, accessToken) });
}

// Fisheries data endpoints (query: stock_uuid, stock_uuids comma-separated)
export const getFisheriesData = async (year = null, stockUuid = null, accessToken) => {
  const params = {};
  if (year) params.year = year;
  if (stockUuid) params.stock_uuid = stockUuid;
  return await api.get('/fisheries-data', { params, headers: withAuth({}, accessToken) });
}

// Table definition endpoint
export const getTableDefinition = async (accessToken) => {
  return await api.get('/table-definition', { headers: withAuth({}, accessToken) });
}

// Management parameters endpoints
export const getManagementParameters = async (accessToken) => {
  return await api.get('/management-parameters', { headers: withAuth({}, accessToken) });
};
export const updateManagementAdjustment = async (stockUuid, data, accessToken) => {
  return await api.put(`/management-adjustment/${stockUuid}`, data, { headers: withAuth({}, accessToken) });
}
export const updatePStarValue = async (stockUuid, data, accessToken) => {
  return await api.put(`/pstar-value/${stockUuid}`, data, { headers: withAuth({}, accessToken) });
}
export const updateAllocationRatio = async (stockUuid, data, accessToken) => {
  return await api.put(`/allocation-ratio/${stockUuid}`, data, { headers: withAuth({}, accessToken) });
}

// BATCH UPDATE FUNCTIONS
export const updateAllPStarValues = async (data, accessToken) => {
  return await api.put('/pstar-value/batch', data, { headers: withAuth({}, accessToken) });
};

export const updateAllManagementAdjustments = async (data, accessToken) => {
  return await api.put('/management-adjustment/batch', data, { headers: withAuth({}, accessToken) });
};

export const updateAllRebuildingTimes = async (data, accessToken) => {
  return await api.put('/rebuilding-time/batch', data, { headers: withAuth({}, accessToken) });
};

// Rebuilding parameters endpoints
export const getRebuildingParameters = async (accessToken) => {
  return await api.get('/rebuilding-parameters', { headers: withAuth({}, accessToken) });
}
export const getStockRebuildingTime = async (stockUuid, accessToken) => {
  return await api.get(`/rebuilding-time/${stockUuid}`, { headers: withAuth({}, accessToken) });
}
export const updateRebuildingTime = async (stockUuid, data, accessToken) => {
  return await api.put(`/rebuilding-time/${stockUuid}`, data, { headers: withAuth({}, accessToken) });
}

// Batch update and other endpoints
export const batchUpdateParameters = async (data, accessToken) => {
  return await api.post('/batch-update-parameters', data, { headers: withAuth({}, accessToken) });
}
export const getMesoscaleDynamicsSettings = async (accessToken) => {
  return await api.get('/mesoscale-dynamics', { headers: withAuth({}, accessToken) });
}
export const updateMesoscaleDynamics = async (data, accessToken) => {
  return await api.put('/mesoscale-dynamics', data, { headers: withAuth({}, accessToken) });
}
export const refreshDatabase = async (accessToken) => {
  return await api.post('/refresh-database', {}, { headers: withAuth({}, accessToken) });
}

// Productivity Enhancement (Zeta) endpoints
export const getProductivityEnhancementSettings = async (accessToken) => {
  return await api.get('/productivity-enhancement', { headers: withAuth({}, accessToken) });
}
export const updateProductivityEnhancement = async (data, accessToken) => {
  return await api.put('/productivity-enhancement', data, { headers: withAuth({}, accessToken) });
}

// Environmental factors endpoint
export const getEnvironmentalFactors = async (accessToken) => {
  return await api.get('/environmental-factors', { headers: withAuth({}, accessToken) });
}

// Model run endpoints
export const runModel = async (params, accessToken) => {
  return await api.post('/run-model', params, { headers: withAuth({}, accessToken) });
}
export const runForecast = async (params, accessToken) => {
  return await api.post('/run-forecast', params, { headers: withAuth({}, accessToken) });
}

// Re-export BAS API for backward compatibility
export { getBasParameterTracking, getBasParameterById } from './basApi';

// Stock Summary endpoint
export const getStockSummary = async (year, timeCategory = null, minDepth = null, maxDepth = null, sedimentType = null, accessToken) => {
  const params = {};
  if (timeCategory) params.time_category = timeCategory;
  if (minDepth !== null) params.min_depth = minDepth;
  if (maxDepth !== null) params.max_depth = maxDepth;
  if (sedimentType) params.predominant_sediment_type = sedimentType;
  const queryString = Object.keys(params).length > 0 ? `?${new URLSearchParams(params).toString()}` : '';
  return await api.get(`/stock_summary/${year}${queryString}`, { headers: withAuth({}, accessToken) });
}

// Helper function to handle API errors
export const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response error:', error.response.data);
    return error.response.data.detail || 'An error occurred with the API.';
  } else if (error.request) {
    // The request was made but no response was received
    console.error('Request error:', error.request);
    return 'No response from server. Please check your connection.';
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error:', error.message);
    return error.message;
  }
};


export const getFisheriesDataBatch = async (years = [], stockUuids = [], accessToken) => {
  const params = {};
  if (years && years.length) params.years = years.join(',');
  if (stockUuids && stockUuids.length) params.stock_uuids = stockUuids.join(',');
  return await api.get('/fisheries-data', { params, headers: withAuth({}, accessToken) });
};

// Trawl Survey legacy functions
export const getTrawlSurveyCruises = async (accessToken) => {
  return await api.get('/trawl-survey/cruise', { headers: withAuth({}, accessToken) });
};

export const getTrawlSurveySpecies = async (accessToken) => {
  return await api.get('/trawl-survey/species', { headers: withAuth({}, accessToken) });
};

export const getTrawlSurveyCruise = async (cruiseId, accessToken) => {
  const params = {};
  if (cruiseId) params.id = cruiseId;
  return await api.get('/trawl-survey/cruise', { params, headers: withAuth({}, accessToken) });
};

export const getTrawlSurveyCatch = async (catchId, accessToken) => {
  return await api.get('/trawl-survey/catch', { params: { 'catch-id': catchId }, headers: withAuth({}, accessToken) });
};

export const getTrawlSurveyCatchesByCruise = async (cruiseId, accessToken) => {
  return await api.get('/trawl-survey/catch', { params: { 'cruise-id': cruiseId }, headers: withAuth({}, accessToken) });
};

export const getTrawlSurveyCatchesByStation = async (stationId, accessToken) => {
  return await api.get('/trawl-survey/catch', { params: { 'station-id': stationId }, headers: withAuth({}, accessToken) });
};

export default api;
// WHAM legacy functions
export const getWhamStocks = async (accessToken) => {
  return await api.get('/wham/stocks', { headers: withAuth({}, accessToken) });
};

export const getWhamSessions = async (stockUuid, accessToken) => {
  return await api.get(`/wham/sessions/${stockUuid}`, { headers: withAuth({}, accessToken) });
};

export const getWhamSession = async (sessionId, accessToken) => {
  return await api.get(`/wham/session/${sessionId}`, { headers: withAuth({}, accessToken) });
};

export const deleteWhamSession = async (sessionId, accessToken) => {
  return await api.delete(`/wham/session/${sessionId}`, { headers: withAuth({}, accessToken) });
};

export const runWhamProjection = async (stockUuid, params, accessToken) => {
  return await api.post(`/wham/run-projection/${stockUuid}`, params, { headers: withAuth({}, accessToken) });
};

export const getLatestWhamProjection = async (stockUuid, accessToken) => {
  return await api.get(`/wham/projection/${stockUuid}/latest`, { headers: withAuth({}, accessToken) });
};

export const getWhamHealth = async (accessToken) => {
  return await api.get('/wham/health', { headers: withAuth({}, accessToken) });
};

export const clearWhamCache = async (accessToken) => {
  return await api.post('/wham/cache/clear', {}, { headers: withAuth({}, accessToken) });
};

// ERDDAP legacy functions
export const getBottomTemperature = async (params, accessToken) => {
  const {
    startDate,
    endDate,
    minLat = 34,
    maxLat = 45,
    minLon = -83,
    maxLon = -66
  } = params || {};
  
  return await api.get('/erddap/bottom-temperature', {
    params: { startDate, endDate, minLat, maxLat, minLon, maxLon },
    headers: withAuth({}, accessToken),
    timeout: 30000
  });
};

// Atlantic States legacy functions
export const getAtlanticStates = async (accessToken) => {
  return await api.get('/atlantic-states', { headers: withAuth({}, accessToken) });
};

export const getAtlanticStatesSummary = async (accessToken) => {
  return await api.get('/atlantic-states/stats/summary', { headers: withAuth({}, accessToken) });
};

export const getAtlanticState = async (state, accessToken) => {
  return await api.get(`/atlantic-states/${state}`, { headers: withAuth({}, accessToken) });
};

// Cold Storage legacy functions
export const getColdStorage = async (accessToken) => {
  return await api.get('/cold-storage', { headers: withAuth({}, accessToken) });
};

// Ports legacy functions
export const getPorts = async (accessToken) => {
  return await api.get('/ports', { headers: withAuth({}, accessToken) });
};

// Page layout legacy function
export const getPageLayout = async (pageType, resourceId, accessToken) => {
  return await api.get('/op/pages/layout', {
    params: { page_type: pageType, resource_id: resourceId },
    headers: withAuth({}, accessToken)
  });
};

// Update page camera legacy function
export const updatePageCamera = async (pageType, resourceId, body, accessToken) => {
  return await api.patch('/op/pages/camera', body, {
    params: { page_type: pageType, resource_id: resourceId },
    headers: withAuth({ 'Content-Type': 'application/json' }, accessToken)
  });
};

// Update page camera for page and all descendants legacy function
export const updatePageCameraWithDescendants = async (pageType, resourceId, body, accessToken) => {
  return await api.patch('/op/pages/camera-with-descendants', body, {
    params: { page_type: pageType, resource_id: resourceId },
    headers: withAuth({ 'Content-Type': 'application/json' }, accessToken)
  });
};

// Update panel layout legacy function
export const updatePanelLayout = async (pageType, resourceId, panelSlug, body, accessToken) => {
  return await api.put('/op/pages/panel-layout', body, {
    params: { page_type: pageType, resource_id: resourceId, panel_slug: panelSlug },
    headers: withAuth({ 'Content-Type': 'application/json' }, accessToken)
  });
};

// Page children legacy function
export const getPageChildren = async (pageType, resourceId, accessToken) => {
  return await api.get('/op/pages/children', {
    params: { page_type: pageType, resource_id: resourceId },
    headers: withAuth({}, accessToken)
  });
};

// Page component slugs legacy function
export const getPageComponentSlugs = async (pageId, accessToken) => {
  return await api.get('/op/pages/component-slugs', {
    params: { page_id: pageId },
    headers: withAuth({}, accessToken)
  });
};

// Panel sections legacy function
export const getPanelSections = async (accessToken) => {
  return await api.get('/op/panel-sections', {
    headers: withAuth({}, accessToken)
  });
};

// Create panel section legacy function
export const createPanelSection = async (body, accessToken) => {
  return await api.post('/op/panel-sections', body, {
    headers: withAuth({ 'Content-Type': 'application/json' }, accessToken)
  });
};

// Page components legacy function
export const getPageComponents = async (accessToken) => {
  return await api.get('/op/components', {
    headers: withAuth({}, accessToken)
  });
};

// Create component legacy function
export const createComponent = async (body, accessToken) => {
  return await api.post('/op/components', body, {
    headers: withAuth({ 'Content-Type': 'application/json' }, accessToken)
  });
};

// Region by slug legacy function
export const getRegionBySlug = async (fisheryManagementRegionSlug, accessToken) => {
  return await api.get('/op/regions/by-slug', {
    params: { fishery_management_region_slug: fisheryManagementRegionSlug },
    headers: withAuth({}, accessToken)
  });
};

// Plans by slugs legacy function
export const getPlansBySlugs = async (fisheryManagementRegionSlug, fisheryManagementPlanSlug, accessToken) => {
  return await api.get('/op/plans/by-slugs', {
    params: { 
      fishery_management_region_slug: fisheryManagementRegionSlug, 
      fishery_management_plan_slug: fisheryManagementPlanSlug 
    },
    headers: withAuth({}, accessToken)
  });
};

// Stocks by slugs legacy function
export const getStocksBySlugs = async (stockSlug, fisheryManagementPlanSlug, fisheryManagementRegionSlug, accessToken) => {
  return await api.get('/op/stocks/by-slugs', {
    params: {
      stock_slug: stockSlug,
      fishery_management_plan_slug: fisheryManagementPlanSlug,
      fishery_management_region_slug: fisheryManagementRegionSlug
    },
    headers: withAuth({}, accessToken)
  });
};
